import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart3,
  Building,
  Settings,
  Calendar,
  Menu,
  ChevronLeft,
  Star,
  LogOut,
  ListChecksIcon,
  Users,
  Goal,
  UserPlus,
  DollarSign,
  Home,
  FileText,
  FileSignature,
  Phone,
  Megaphone,
  TrendingUp,
  Trash2,
  LifeBuoy,
  Sparkles,
  ShieldCheck,
  Database,
  ClipboardList,
  Send,
  Bug,
  Store,
  Compass,
  Target,
  RotateCcw,
  Building2,
  FileBarChart2,
  Fingerprint,
} from "lucide-react";
import collingsLogo from "../../assets/collingslogo.png";
import { logout } from "../../store/slices/authSlice";
import { useState } from "react";
import { useNotifications } from "../../contexts/NotificationContext";
import UserAvatar from "../common/UserAvatar";
import { hasAdminAccess, hasSuperAdminAccess, roleLabel } from "../../utils/roleAccess";
import useMyTeam from "../../hooks/useMyTeam";
import { REPORTS_ROUTE_PREFIXES } from "../../features/reports/reportsCatalog";

// Agents allowed to see the Campaign Manager tab (lower-cased emails).
const CAMPAIGN_MANAGER_AGENT_PILOT = ["simon@collings.com.au"];

/**
 * The Prospecting tab — everything an agent does to WIN a listing, in the order
 * Christian's Ignite screenshots show it.
 *
 * Search, Garage Sales, Communications and AI Voice Caller point at the pages
 * that already exist; the ticket asked to move them under Prospecting, so they
 * are re-parented here rather than duplicated. `sectionLabel` starts a sub
 * heading within the tab (Pipeline, Tools).
 *
 * `comingSoon` closes a row off: it shows a SOON pill and cannot be clicked.
 * Christian asked for this on 15 Aug 2026 (Monday 12780827140) for Search,
 * Market Update, CMA and AI Voice Caller, because he still has edits coming for
 * those pages and does not want agents in them yet (Market Update and CMA now
 * sit behind the Reports tab, see reportsCatalog.js). Admins keep access so the
 * pages can still be reviewed while those edits land — see COMING_SOON_ROUTES
 * in App.js, which closes the routes themselves the same way.
 */
const PROSPECTING_NAV_ITEM = {
  id: "/prospecting",
  label: "Prospecting",
  icon: Compass,
  description: "Find the next listing: search, buyer match, and vendor reports",
  subItems: [
    // Pipeline used to be its own top-level tab. Christian asked for it to
    // become a sub group inside Prospecting (Monday 12837881780, 18 Aug 2026),
    // and later for it to be the first group in the tab (Monday 12988442847).
    // The unread badges on these rows are filled in by the navItems memo
    // below, because the counts come from the notification context.
    //
    // Database, Seller Leads, Off Market Pipeline and On Market Pipeline are
    // now ONE row, Prospects, with a Pipeline / Database toggle inside the page
    // (Christian Gravias, Monday 12988442847, 29 Sep 2026: "We are bringing
    // database into this 1 page and also combining on market & off market
    // leads"). The four pages keep their routes, and `aliases` lights this row
    // up on any of them, so a link into an old page still shows where you are.
    {
      sectionLabel: "Pipeline",
      id: "/prospects",
      label: "Prospects",
      icon: Users,
      aliases: ["/database", "/seller-leads", "/off-market-pipeline", "/on-market-pipeline"],
    },
    { id: "/leads", label: "Buyer Enquiries & Offers", icon: Star },
    // The prospecting rows sit under Pipeline, which is first in the tab
    // (Christian Gravias, Monday 12988442847, 1 Oct 2026: "Move Pipeline to the
    // top of the PROSPECTING tab"). They get their own heading so they don't
    // read as part of the Pipeline group.
    // Christian, Monday 12573204628 (12 Oct 2026): renamed from "Search" and
    // opened up now the Homer data is in.
    { id: "/collings-data-properties", label: "Public Property Search", icon: Database, sectionLabel: "Tools" },
    { id: "/prospecting/find-buyers", label: "Find Buyers", icon: Target },
    // Christian, Monday 13202980753 (12 Oct 2026): its own row between Find
    // Buyers and Garage Sales; the dashboard card's "View All" lands here too.
    { id: "/prospecting/relisting-opportunities", label: "Re-listing Opportunities", icon: RotateCcw },
    // Christian, Monday 12899126094 (23 Oct 2026): BCI projects, right under
    // Re-listing; the dashboard tile's "View All" lands here too.
    { id: "/prospecting/new-developments", label: "New Developments", icon: Building2 },
    { id: "/garage-sales", label: "Garage Sales", icon: Store },
    { id: "/communications", label: "Communications", icon: Send },
    { id: "/ai-voice-caller", label: "AI Voice Caller", icon: Phone, comingSoon: true },
    // Market Update and CMA used to sit here under a "Reports" heading. They
    // moved to their own Reports tab below (Monday 13377706735).
    // DOMA Proposals (Christian, 15 Oct 2026): vendor proposals built in the CRM.
    // Ships admin-locked (SOON for agents) until he opens it to the team.
    { id: "/prospecting/proposals", label: "Proposals", icon: FileSignature, comingSoon: true },
  ],
};

/**
 * Christian, Monday 13377706735 (25 Oct 2026): "Make it 1 tab called REPORTS".
 * One row, no sub menu; the page behind it holds the five report buttons.
 * The report pages kept their /prospecting/... routes, so `activePrefixes`
 * keeps this tab lit while an agent is inside one of them.
 */
const REPORTS_NAV_ITEM = {
  id: "/reports",
  label: "Reports",
  icon: FileBarChart2,
  description: "Market Update, Listing Proposals, CMA and Price Statement",
  activePrefixes: REPORTS_ROUTE_PREFIXES,
};

const Navigation = ({
  isCollapsed,
  toggleCollapse,
  setIsCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    unreadCount,
    buyerEnquiryCount,
    prospectsNewCount,
    messengerUnread,
  } = useNotifications();
  const isAdmin = hasAdminAccess(user);
  const isSuperAdmin = hasSuperAdminAccess(user);
  // A team lead gets Agent Performance for their own team (Monday 13225574145).
  const { hasTeam } = useMyTeam();
  // Support Tickets is a tech/super-admin-only tool (CRM-2026-00034); agents
  // and plain admins see neither the nav item nor the route.
  const isPA = user?.role === "pa";
  const paAccessLevel = user?.pa_access_level;
  const isPropertiesOnlyPA = isPA && paAccessLevel === 'properties_only';
  // Campaign Manager is an admin tab. It is being opened to agents one at a
  // time, starting with a single pilot agent (22 Oct 2026). Add an address to
  // CAMPAIGN_MANAGER_AGENT_PILOT to let another agent in; the /campaign-manager
  // route itself is not role-gated, so the sidebar entry is the only switch.
  const canSeeCampaignManager = CAMPAIGN_MANAGER_AGENT_PILOT.includes(
    String(user?.email || "").trim().toLowerCase()
  );
  const [propertiesSubOpen, setPropertiesSubOpen] = useState(false);
  const [trashSubOpen, setTrashSubOpen] = useState(false);
  const [prospectingSubOpen, setProspectingSubOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const closeMobileMenu = () => {
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  // Brand Colors
  const brandColors = {
    primary: "#1F2937", // Darker for better visibility
    secondary: "#55C5D0",
  };

  const adminNavItems = [
    {
      id: "/",
      label: "Dashboard",
      icon: BarChart3,
      description: "Overview and analytics",
    },
    // AI Voice Caller used to sit here as its own tab with Call Center / Voice
    // Agents / Campaign Setup under it. It now lives inside Prospecting, under
    // Communications, and is closed off (Monday 12780827140). Its routes still
    // exist, so an admin reviewing it lands on the Call Center as before.
    {
      id: "/campaign-manager",
      label: "Campaign Manager",
      icon: Megaphone,
      description: "Manage marketing campaigns",
    },
    {
      id: "/properties",
      label: "Properties",
      icon: Building,
      description: "Property management",
      subItems: [
        {
          id: "/properties",
          label: "My Listings",
          icon: Building,
        },
        {
          id: "/collings-properties",
          label: "Collings Properties",
          icon: Building,
        },
        // Public Properties has no nav entry on purpose. Christian Gravias,
        // CRM-2026-00031 (16 Sep 2026): "Please also hide the public properties
        // tab. Its not needed for now." The layer itself is untouched - the
        // Database rows are reached from the dashboard search bar, which marks
        // them as Public Properties, and /public-properties still serves them.
      ],
    },
    PROSPECTING_NAV_ITEM,
    REPORTS_NAV_ITEM,
    {
      id: "/contacts",
      label: "Contacts",
      icon: UserPlus,
      description: "Contact management",
    },
    {
      id: "/analytics/agent",
      label: "Agent Performance",
      icon: TrendingUp,
      description: "Track an agent's performance — toggle between agents",
    },
    {
      // Agents and Access & Permissions are one tab (Monday 13058000755,
      // Christian Gravias, 16 Sep 2026). The permissions page is now a tab
      // inside Agent Management; /access-permissions still lands on it.
      id: "/agents",
      label: "Agents Access & Permissions",
      icon: ShieldCheck,
      description: "Agents, plus who holds which CRM access level",
    },
    // "Users" (buyer-portal accounts) no longer has its own sidebar tab. It
    // moved inside Contacts: the "Portal Users" card on the Contacts page
    // opens it (same Monday item).
    {
      id: "/appraisal-submissions",
      label: "Appraisal Forms",
      icon: ClipboardList,
      description: "Vendor answers from the Block of Units intake form",
    },
    {
      id: "/tasks",
      label: "Tasks",
      icon: ListChecksIcon,
      description: "Task management",
    },
    {
      id: "/kpi",
      label: "KPI & Goals",
      icon: Goal,
      description: "Track your performance goals",
    },
    {
      id: "/commissions",
      label: "Leaderboard",
      icon: DollarSign,
      description: "Agency leaderboard and my stats",
    },
    {
      id: "/documents",
      label: "Documents",
      icon: FileText,
      description: "Property and campaign documents",
    },
    {
      id: "/esign",
      label: "eSign",
      icon: FileSignature,
      description: "Send documents for e-signature",
    },
    {
      id: "/calendar",
      label: "Calendar",
      icon: Calendar,
      description: "Manage your schedule",
    },
    {
      id: "/inspections",
      label: "Inspections",
      icon: Home,
      description: "Property inspections",
    },
    {
      id: "/analytics",
      label: "Analytics",
      icon: TrendingUp,
      description: "Buyer portal analytics and insights",
    },
    ...(isSuperAdmin ? [{
      id: "/support-tickets",
      label: "Support Tickets",
      icon: LifeBuoy,
      description: "Issues and feedback from the Buyer Portal",
    }] : []),
    ...(isSuperAdmin ? [{
      id: "/property-id-review",
      label: "Property ID Review",
      icon: Fingerprint,
      description: "Uncertain Property ID matches for the data team",
    }] : []),
    ...(isSuperAdmin ? [{
      id: "/ai-seo",
      label: "AI-SEO",
      icon: Sparkles,
      description: "Publish first-party property content from the Property Brain",
    }] : []),
    {
      id: "/report-issue",
      label: "Report an Issue",
      icon: Bug,
      description: "Report a CRM problem straight to the Monday board",
    },
    {
      id: "/settings",
      label: "Settings",
      icon: Settings,
      description: "System settings and configuration",
    },
    ...(isAdmin ? [{
      id: "/trash",
      label: "Trash",
      icon: Trash2,
      description: "Deleted properties and contacts",
      subItems: [
        {
          // Covers both My Listings and Collings Properties deletes in one
          // combined list - having two separate nav entries for what's really
          // the same concept (a deleted property) confused agents into
          // thinking they were duplicates of each other (CG feedback,
          // 2026-08-13). The two underlying datasets/restore endpoints are
          // still genuinely separate under the hood; PropertyTrashPage merges
          // them client-side.
          id: "/properties/trash",
          label: "Property Trash",
          icon: Trash2,
        },
        {
          id: "/contacts/trash",
          label: "Contact Trash",
          icon: Trash2,
        },
      ],
    }] : []),
  ];

  const agentNavItems = [
    {
      id: "/",
      label: "Dashboard",
      icon: BarChart3,
      description: "Your performance overview",
    },
    // Spiros Vamvalis, CRM-2026-00107 (12 Oct 2026): an agent with a team sees
    // themselves under Agent Performance, so My Performance is a duplicate tab.
    // Solo agents keep it. The /my-performance route itself stays, so the
    // dashboard tiles that deep-link into it still work.
    ...(!hasTeam ? [{
      id: "/my-performance",
      label: "My Performance",
      icon: TrendingUp,
      description: "Your full analytics breakdown and rank",
    }] : []),
    ...(hasTeam ? [{
      id: "/analytics/agent",
      label: "Agent Performance",
      icon: TrendingUp,
      description: "Your team's performance - toggle between team members",
    }] : []),
    ...(canSeeCampaignManager ? [{
      id: "/campaign-manager",
      label: "Campaign Manager",
      icon: Megaphone,
      description: "Manage marketing campaigns",
    }] : []),
    {
      id: "/properties",
      label: "Properties",
      icon: Building,
      description: "Property management",
      subItems: [
        {
          id: "/properties",
          label: "My Listings",
          icon: Building,
        },
        {
          id: "/collings-properties",
          label: "Collings Properties",
          icon: Building,
        },
        // Public Properties has no nav entry on purpose. Christian Gravias,
        // CRM-2026-00031 (16 Sep 2026): "Please also hide the public properties
        // tab. Its not needed for now." The layer itself is untouched - the
        // Database rows are reached from the dashboard search bar, which marks
        // them as Public Properties, and /public-properties still serves them.
      ],
    },
    PROSPECTING_NAV_ITEM,
    REPORTS_NAV_ITEM,
    {
      id: "/contacts",
      label: "Contacts",
      icon: UserPlus,
      description: "Contact management",
    },
    {
      id: "/tasks",
      label: "Tasks",
      icon: ListChecksIcon,
      description: "Task management",
    },
    {
      id: "/kpi",
      label: "KPI & Goals",
      icon: Goal,
      description: "Track your performance goals",
    },
    {
      id: "/commissions",
      label: "Leaderboard",
      icon: DollarSign,
      description: "Agency leaderboard and my stats",
    },
    {
      id: "/documents",
      label: "Documents",
      icon: FileText,
      description: "Property and campaign documents",
    },
    {
      id: "/esign",
      label: "eSign",
      icon: FileSignature,
      description: "Send documents for e-signature",
    },
    {
      id: "/calendar",
      label: "Calendar",
      icon: Calendar,
      description: "Manage your schedule",
    },
    {
      id: "/inspections",
      label: "Inspections",
      icon: Home,
      description: "Property inspections",
    },
    {
      id: "/analytics",
      label: "Analytics",
      icon: TrendingUp,
      description: "Buyer portal analytics and insights",
    },
    {
      id: "/report-issue",
      label: "Report an Issue",
      icon: Bug,
      description: "Report a CRM problem straight to the Monday board",
    },
    {
      id: "/settings",
      label: "Settings",
      icon: Settings,
      description: "System settings and configuration",
    },
  ];

  // Nav for PA with 'properties_only' access — only Properties (no dashboard, calendar, inspections, settings)
  const paPropertiesOnlyNavItems = [
    {
      id: "/properties",
      label: "Properties",
      icon: Building,
      description: "Property management",
      subItems: [
        { id: "/properties", label: "My Listings", icon: Building },
        { id: "/collings-properties", label: "Collings Properties", icon: Building },
        { id: "/collings-data-properties", label: "Public Property Search", icon: Database },
      ],
    },
  ];

  const navItems = useMemo(() => {
    const base = isPropertiesOnlyPA
      ? paPropertiesOnlyNavItems
      : (isAdmin || isPA ? adminNavItems : agentNavItems);
    // Messenger lives inside the Communications page, and the Pipeline rows now
    // live inside Prospecting too, so every one of these badges has to travel
    // two levels down — otherwise an unread message or a new enquiry becomes
    // invisible in the sidebar.
    // Prospects counts what its own "N new" pill counts: seller leads nobody
    // has called yet.
    const counts = {
      '/communications': messengerUnread || 0,
      '/leads': buyerEnquiryCount || 0,
      '/prospects': prospectsNewCount || 0,
    };
    return base.map((item) => {
      if (!item.subItems?.some((sub) => counts[sub.id] !== undefined)) return item;
      const subItems = item.subItems.map((sub) =>
        counts[sub.id] !== undefined ? { ...sub, notifCount: counts[sub.id] } : sub
      );
      return {
        ...item,
        // Roll the total up to the parent too, so it shows while collapsed.
        notifCount: subItems.reduce((sum, sub) => sum + (sub.notifCount || 0), 0),
        subItems,
      };
    });
  }, [
    isAdmin,
    isSuperAdmin,
    hasTeam,
    isPA,
    isPropertiesOnlyPA,
    canSeeCampaignManager,
    messengerUnread,
    buyerEnquiryCount,
    prospectsNewCount,
  ]);

    const handleLogoClick = () => {
      if (location.pathname === "/") {
        window.location.reload();
      } else {
        navigate("/");
      }
    };

    return (
      <>
        {/* Mobile Top Bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-gray-200 shadow-sm flex items-center px-3">
          <button
            onClick={() => setIsMobileMenuOpen && setIsMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          <div
            className="ml-2 flex items-center cursor-pointer"
            onClick={handleLogoClick}
          >
            <img src={collingsLogo} alt="Collings" className="h-6 w-auto object-contain" />
          </div>
        </div>
  
        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeMobileMenu}
          />
        )}
  
        {/* Sidebar */}
        <div
          className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-100 flex flex-col transition-all duration-300 z-50 ${isCollapsed ? "w-20" : "w-64"
            } ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0`}
        >
          <div className="p-4 border-b border-gray-100">
            <div className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}>
              <div 
                className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleLogoClick}
                title="Return to Home"
              >
                {!isCollapsed && (
                  <img
                    src={collingsLogo}
                    alt="Collings Logo"
                    className="h-6 w-auto object-contain"
                  />
                )}
              </div>
            <button
              onClick={toggleCollapse}
              className="p-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600"
            >
              {isCollapsed ? (
                <Menu className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 py-6 overflow-y-auto">
          <div className="space-y-2 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.id ||
                Boolean(item.activePrefixes?.some(
                  (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
                )) ||
                (item.subItems &&
                  item.subItems.some(
                    (sub) => location.pathname === sub.id || sub.aliases?.includes(location.pathname)
                  ));
              if (item.subItems) {
                // Handle Properties, Prospecting and Trash sub-menus
                // One open/close flag per expandable tab, keyed by its route.
                const SUB_MENU_STATE = {
                  "/properties": [propertiesSubOpen, setPropertiesSubOpen],
                  "/prospecting": [prospectingSubOpen, setProspectingSubOpen],
                  "/trash": [trashSubOpen, setTrashSubOpen],
                };
                const [subOpen, setSubOpen] = SUB_MENU_STATE[item.id] || [false, () => { }];
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        if (isCollapsed && setIsCollapsed) {
                          setIsCollapsed(false);
                          setTimeout(() => setSubOpen(true), 300); // Wait for expand animation
                        } else {
                          setSubOpen((prev) => !prev);
                        }
                      }}
                      className={`w-full flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 relative ${isActive
                        ? "text-white shadow-lg transform -translate-y-0.5"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      style={{
                        backgroundColor: isActive
                          ? brandColors.secondary
                          : "transparent",
                        boxShadow: isActive
                          ? "0 4px 20px rgba(85, 197, 208, 0.3)"
                          : "none",
                      }}
                    >
                      <Icon className="w-5 h-5" />
                      {!isCollapsed && (
                        <>
                          <span className="ml-3">{item.label}</span>
                          {item.badge && (
                            <span
                              className={`absolute right-2 px-2 py-0.5 text-xs rounded-full ${item.badge === "Live"
                                ? "bg-green-100 text-green-700"
                                : "bg-blue-100 text-blue-700"
                                }`}
                            >
                              {item.badge}
                            </span>
                          )}
                          {/* Rolled up from a sub-item (e.g. unread messenger
                              threads under Communications), so the count is
                              still visible with the tab collapsed. */}
                          {item.notifCount > 0 && (
                            <span className="ml-auto mr-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                              {item.notifCount > 99 ? '99+' : item.notifCount}
                            </span>
                          )}
                          <span
                            className={`${item.notifCount > 0 ? "" : "ml-auto"} transition-transform ${subOpen ? "rotate-90" : ""
                              }`}
                          >
                            ▶
                          </span>
                        </>
                      )}
                    </button>
                    {/* Sub-menu */}
                    {subOpen && !isCollapsed && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subItems.map((sub) => {
                          const SubIcon = sub.icon;
                          const isSubActive = location.pathname === sub.id ||
                            location.pathname + location.search === sub.id ||
                            Boolean(sub.aliases?.includes(location.pathname));
                          // Closed off until Christian signs the page off. Admins
                          // keep the link so they can review it; everyone else
                          // gets a greyed row they cannot click.
                          const isLocked = sub.comingSoon && !isAdmin;
                          return (
                            <React.Fragment key={sub.id}>
                            {/* A sub heading inside the tab, e.g. "Reports" under
                                Prospecting. Mirrors the Ignite layout. */}
                            {sub.sectionLabel && (
                              <p className="pt-3 pb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                {sub.sectionLabel}
                              </p>
                            )}
                            <button
                              onClick={() => {
                                if (isLocked) return;
                                navigate(sub.id);
                                closeMobileMenu();
                              }}
                              disabled={isLocked}
                              aria-disabled={isLocked || undefined}
                              title={isLocked ? `${sub.label} is coming soon` : undefined}
                              className={`w-full flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${isLocked
                                ? "text-gray-400 cursor-not-allowed"
                                : isSubActive
                                  ? "bg-[#55C5D0] text-white"
                                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span className="ml-2 flex-1 text-left">{sub.label}</span>
                              {sub.comingSoon && (
                                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide bg-amber-100 text-amber-700">
                                  SOON
                                </span>
                              )}
                              {sub.notifCount > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                                  {sub.notifCount > 99 ? '99+' : sub.notifCount}
                                </span>
                              )}
                            </button>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              // Normal nav item
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.id);
                    closeMobileMenu();
                  }}
                  className={`w-full flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 relative
                  ${isActive
                      ? "text-white shadow-lg transform -translate-y-0.5"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  style={{
                    backgroundColor: isActive
                      ? brandColors.secondary
                      : "transparent",
                    boxShadow: isActive
                      ? "0 4px 20px rgba(85, 197, 208, 0.3)"
                      : "none",
                  }}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && (
                    <>
                      <span className="ml-3 flex-1 text-left">{item.label}</span>
                      {item.notifCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                          {item.notifCount > 99 ? '99+' : item.notifCount}
                        </span>
                      )}
                      {item.badge && (
                        <span
                          className={`absolute right-2 px-2 py-0.5 text-xs rounded-full ${item.badge === "Live"
                            ? "bg-green-100 text-green-700"
                            : "bg-blue-100 text-blue-700"
                            }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {isCollapsed && item.notifCount > 0 && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* User Profile and Logout */}
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                navigate("/my-profile");
                closeMobileMenu();
              }}
              className="flex items-center space-x-3 flex-1 min-w-0 text-left rounded-lg hover:bg-gray-50 p-1 -m-1 transition-colors"
              title="Edit your profile"
            >
              {!isCollapsed && (
                <UserAvatar
                  src={user?.profile_pic_url || user?.profile_pic}
                  name={user?.name}
                  size={40}
                  bgColor={brandColors.primary}
                />
              )}
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name || "User"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role ? roleLabel(user.role) : "Agent"}
                  </p>
                </div>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
